import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CalendarDaysIcon, ClockIcon, MapPinIcon, TagIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { eventSchema, type EventData } from '../lib/validation';
import { createEvent, updateEvent, isValidUUID } from '../lib/supabase';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: (EventData & { id: string }) | null;
  tournamentId: string;
  onSuccess?: () => void;
}

const EVENT_TYPES = [
  { value: 'Registration', color: 'bg-sky-50 border-sky-300 text-sky-700 ring-sky-200' },
  { value: 'Tournament', color: 'bg-orange-50 border-orange-300 text-orange-700 ring-orange-200' },
  { value: 'Ceremony', color: 'bg-amber-50 border-amber-300 text-amber-700 ring-amber-200' },
  { value: 'Social', color: 'bg-emerald-50 border-emerald-300 text-emerald-700 ring-emerald-200' },
];

export default function EventModal({ isOpen, onClose, event, tournamentId, onSuccess }: EventModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState('Tournament');

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<EventData>({
    resolver: zodResolver(eventSchema),
    defaultValues: { tournament_id: tournamentId, title: '', date: '', time: '', location: '', type: 'Tournament', description: '' }
  });

  const watchedType = watch('type');

  useEffect(() => {
    if (event) {
      setValue('tournament_id', event.tournament_id);
      setValue('title', event.title);
      setValue('date', event.date);
      setValue('time', event.time);
      setValue('location', event.location);
      setValue('type', event.type);
      setValue('description', event.description || '');
      setSelectedType(event.type);
    } else {
      reset({ tournament_id: tournamentId, title: '', date: '', time: '', location: '', type: 'Tournament', description: '' });
      setSelectedType('Tournament');
    }
  }, [event, tournamentId, reset, setValue]);

  const handleClose = () => { setError(''); reset(); onClose(); };

  const onSubmit = async (data: EventData) => {
    try {
      setIsSubmitting(true);
      setError('');
      if (!isValidUUID(data.tournament_id)) throw new Error('Invalid tournament ID format');
      if (event) {
        const { error: err } = await updateEvent(event.id, data);
        if (err) throw err;
      } else {
        const { error: err } = await createEvent(data);
        if (err) throw err;
      }
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-golf-orange focus:bg-white focus:ring-2 focus:ring-golf-orange/10 transition-all';

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-golf-orange/10 flex items-center justify-center">
                      <CalendarDaysIcon className="w-5 h-5 text-golf-orange" />
                    </div>
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      {event ? 'Edit Event' : 'Create Event'}
                    </Dialog.Title>
                  </div>
                  <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
                    <XMarkIcon className="w-4 h-4 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="px-6 py-5 space-y-4">
                    {error && (
                      <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-600 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                        {error}
                      </div>
                    )}

                    <input type="hidden" {...register('tournament_id')} value={tournamentId} />

                    {/* Title */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Event Title <span className="text-red-400">*</span></label>
                      <input type="text" {...register('title')} placeholder="e.g. Opening Ceremony" className={inputClass} />
                      {errors.title && <p className="text-xs text-red-500">{errors.title.message}</p>}
                    </div>

                    {/* Date + Time */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <CalendarDaysIcon className="w-3 h-3" /> Date <span className="text-red-400">*</span>
                        </label>
                        <input type="date" {...register('date')} className={inputClass} />
                        {errors.date && <p className="text-xs text-red-500">{errors.date.message}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <ClockIcon className="w-3 h-3" /> Time <span className="text-red-400">*</span>
                        </label>
                        <input type="time" {...register('time')} className={inputClass} />
                        {errors.time && <p className="text-xs text-red-500">{errors.time.message}</p>}
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <MapPinIcon className="w-3 h-3" /> Location <span className="text-red-400">*</span>
                      </label>
                      <input type="text" {...register('location')} placeholder="e.g. Clubhouse" className={inputClass} />
                      {errors.location && <p className="text-xs text-red-500">{errors.location.message}</p>}
                    </div>

                    {/* Event Type - pill grid */}
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <TagIcon className="w-3 h-3" /> Event Type <span className="text-red-400">*</span>
                      </label>
                      <input type="hidden" {...register('type')} />
                      <div className="grid grid-cols-4 gap-2">
                        {EVENT_TYPES.map(t => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => { setSelectedType(t.value); setValue('type', t.value as EventData['type']); }}
                            className={`py-2 px-1 rounded-xl border text-xs font-semibold text-center transition-all ${
                              watchedType === t.value ? `${t.color} ring-2` : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                            }`}
                          >
                            {t.value}
                          </button>
                        ))}
                      </div>
                      {errors.type && <p className="text-xs text-red-500">{errors.type.message}</p>}
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <DocumentTextIcon className="w-3 h-3" /> Description
                      </label>
                      <textarea {...register('description')} rows={3} placeholder="Optional details..." className={`${inputClass} resize-none`} />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <button type="button" onClick={handleClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white hover:bg-gray-50 transition-colors">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting || !isValidUUID(tournamentId)} className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-semibold hover:bg-golf-orange/90 disabled:opacity-50 shadow-sm shadow-golf-orange/20 transition-all">
                      {isSubmitting ? 'Saving...' : event ? 'Save Changes' : 'Create Event'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
